'use client'

import { useState, useEffect } from 'react'
import { getDetailedMetrics, Asset } from './riskAssessment'
import {
  recommendStrategy,
  HedgingStrategy,
  getStrategyDescription
} from './strategy'
import { executeHedge } from './execute'
import { monitorHedge } from './monitor'
import { calculateRiskMetrics, RiskMetrics } from './riskMetrics'
import styles from './Hedging.module.css'

/**
 * @uiComponent
 * @name Hedging
 * @description The Hedging component provides comprehensive portfolio risk management and hedging strategy implementation.
 * It analyzes portfolio risk metrics, industry exposure, and recommends appropriate hedging strategies like puts, inverse ETFs,
 * collar strategies, and pairs trading. Features real-time risk monitoring, strategy execution, and performance tracking.
 * @location Hedging plugin in the trading terminal
 * @prop none - No external props, manages internal portfolio state and risk calculations
 */

/**
 * @uiWorkflow
 * @name Analyze Portfolio Risk and Get Hedging Recommendations
 * @description How to assess portfolio risk and receive automated hedging strategy suggestions
 * @platform hedging
 * @step 1 Navigate to the Hedging plugin [component: Hedging] [action: navigate]
 * @step 2 Wait for portfolio data to load automatically [component: Hedging] [action: wait] [notes: Portfolio fetched from API]
 * @step 3 Review the portfolio value and risk score [component: Hedging] [action: observe] [notes: Risk score ranges from 0-100]
 * @step 4 Examine volatility and beta metrics [component: Hedging] [action: read] [notes: Higher values indicate more risk]
 * @step 5 Check Value at Risk (VaR) calculation [component: Hedging] [action: observe] [notes: 95% confidence level]
 * @step 6 Review market correlations table [component: Hedging] [action: scroll]
 * @step 7 Analyze industry exposure breakdown [component: Hedging] [action: examine] [notes: Identify concentration risks]
 * @step 8 Review recommended hedging strategy [component: Hedging] [action: read] [notes: Auto-selected based on risk]
 * @step 9 Click "Execute Selected Strategy" if desired [component: Hedging] [action: click]
 * @step 10 Monitor execution message for confirmation [component: Hedging] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Select and Execute Custom Hedging Strategy
 * @description How to manually choose and implement specific hedging strategies
 * @platform hedging
 * @step 1 Load the Hedging dashboard [component: Hedging] [action: navigate]
 * @step 2 Scroll down to the Hedging Strategies section [component: Hedging] [action: scroll]
 * @step 3 Review available strategy options [component: Hedging] [action: read] [notes: Buy Puts, Inverse ETF, Collar, etc.]
 * @step 4 Click on your preferred strategy card [component: Hedging] [action: click] [notes: Card will highlight when selected]
 * @step 5 Read the strategy description [component: Hedging] [action: read] [notes: Understand the approach and risks]
 * @step 6 Compare with current portfolio metrics [component: Hedging] [action: analyze]
 * @step 7 Click "Execute Selected Strategy" button [component: Hedging] [action: click]
 * @step 8 Wait for execution confirmation [component: Hedging] [action: wait]
 * @step 9 Review the execution message [component: Hedging] [action: read]
 * @step 10 Monitor portfolio changes in real-time [component: Hedging] [action: monitor]
 */

/**
 * @uiWorkflow
 * @name Monitor Portfolio Holdings and Risk Metrics
 * @description How to track individual positions and their risk contributions
 * @platform hedging
 * @step 1 Access the Hedging dashboard [component: Hedging] [action: navigate]
 * @step 2 Locate the Portfolio Holdings table [component: Hedging] [action: scroll]
 * @step 3 Review each stock's basic information [component: Hedging] [action: read] [notes: Symbol, shares, industry]
 * @step 4 Compare average price vs current price [component: Hedging] [action: compare]
 * @step 5 Identify profitable vs losing positions [component: Hedging] [action: analyze] [notes: Green = profit, Red = loss]
 * @step 6 Check industry concentration [component: Hedging] [action: examine] [notes: Look for over-exposure]
 * @step 7 Calculate total portfolio P&L [component: Hedging] [action: sum]
 * @step 8 Refresh data periodically [component: Hedging] [action: refresh] [notes: Data updates automatically]
 */

export default function HedgingPage() {
  const [portfolio, setPortfolio] = useState<Asset[]>([])
  const [totalValue, setTotalValue] = useState<number>(0)
  const [industryExposure, setIndustryExposure] = useState<{
    [key: string]: number
  }>({})
  const [riskScore, setRiskScore] = useState<number>(0)
  const [strategy, setStrategy] = useState<HedgingStrategy>('none')
  const [executionMessage, setExecutionMessage] = useState<string>('')
  const [riskMetrics, setRiskMetrics] = useState<RiskMetrics | null>(null)
  const [selectedStrategy, setSelectedStrategy] =
    useState<HedgingStrategy>('none')

  // Fetch portfolio data from our API route.
  const fetchPortfolio = async () => {
    try {
      const res = await fetch('/api/portfolio')
      const data = (await res.json()) as { portfolio: Asset[] }
      setPortfolio(data.portfolio)
    } catch (error) {
      console.error('Error fetching portfolio data:', error)
    }
  }

  useEffect(() => {
    fetchPortfolio()
    monitorHedge()
  }, [])

  // Recalculate risk metrics and strategy whenever the portfolio changes.
  useEffect(() => {
    if (portfolio.length) {
      const { totalValue, industryExposure, riskScore } =
        getDetailedMetrics(portfolio)
      const metrics = calculateRiskMetrics(portfolio)
      setTotalValue(totalValue)
      setIndustryExposure(industryExposure)
      setRiskMetrics(metrics)
      setRiskScore(riskScore)
      const recStrategy = recommendStrategy(riskScore, metrics.beta)
      setStrategy(recStrategy)
      setSelectedStrategy(recStrategy)
    }
  }, [portfolio])

  const handleExecute = async () => {
    if (strategy === 'none') {
      setExecutionMessage('No hedging required for low risk.')
      return
    }
    const response = await executeHedge(strategy)
    setExecutionMessage(response.message)
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Portfolio Hedging Dashboard</h1>
      </div>

      <div className={styles.metricsGrid}>
        <div className={styles.metricCard}>
          <h3>Portfolio Value</h3>
          <h2>₹{totalValue.toFixed(2)}</h2>
        </div>
        <div className={styles.metricCard}>
          <h3>Risk Score</h3>
          <h2>{riskScore.toFixed(2)}/100</h2>
        </div>
        {riskMetrics && (
          <>
            <div className={styles.metricCard}>
              <h3>Volatility</h3>
              <h2>{riskMetrics.volatility.toFixed(2)}%</h2>
            </div>
            <div className={styles.metricCard}>
              <h3>Portfolio Beta</h3>
              <h2>{riskMetrics.beta.toFixed(2)}</h2>
            </div>
          </>
        )}
      </div>

      <div className={styles.riskMetricsCard}>
        <h2>Risk Analysis</h2>
        {riskMetrics && (
          <>
            <p>Value at Risk (95%): ₹{riskMetrics.valueAtRisk.toFixed(2)}</p>
            <h3>Market Correlations</h3>
            <div className={styles.correlationGrid}>
              {Object.entries(riskMetrics.correlations).map(([index, corr]) => (
                <div key={index} className={styles.correlationItem}>
                  <strong>{index}</strong>
                  <div>{(corr * 100).toFixed(2)}%</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <h2>Industry Exposure</h2>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Industry</th>
            <th>Exposure (%)</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(industryExposure).map(([industry, exposure]) => (
            <tr key={industry}>
              <td>{industry}</td>
              <td>{exposure.toFixed(2)}%</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Portfolio Holdings</h2>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Shares</th>
            <th>Industry</th>
            <th>Avg Price</th>
            <th>Current Price</th>
            <th>PnL</th>
          </tr>
        </thead>
        <tbody>
          {portfolio.map(asset => (
            <tr key={asset.symbol}>
              <td>{asset.symbol}</td>
              <td>{asset.shares}</td>
              <td>{asset.industry}</td>
              <td>₹{asset.avgPrice}</td>
              <td>₹{asset.currentPrice}</td>
              <td style={{ color: asset.pnl >= 0 ? '#4CAF50' : '#ff4444' }}>
                ₹{asset.pnl}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Hedging Strategies</h2>
      <div className={styles.strategyGrid}>
        {[
          'buy_puts',
          'inverse_etf',
          'collar_strategy',
          'beta_hedging',
          'pairs_trading',
          'delta_neutral',
          'none'
        ].map(strat => (
          <div
            key={strat}
            className={`${styles.strategyCard} ${
              selectedStrategy === strat ? styles.selected : ''
            }`}
            onClick={() => {
              setSelectedStrategy(strat as HedgingStrategy)
              setStrategy(strat as HedgingStrategy)
            }}
          >
            <h3>{strat.replace('_', ' ').toUpperCase()}</h3>
            <p>{getStrategyDescription(strat as HedgingStrategy)}</p>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '24px', textAlign: 'center' }}>
        <button
          className={styles.executeButton}
          onClick={handleExecute}
          disabled={strategy === 'none'}
        >
          Execute Selected Strategy
        </button>
        {executionMessage && (
          <div className={styles.message}>{executionMessage}</div>
        )}
      </div>
    </div>
  )
}
